import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { startCase } from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';
import { sendGraph } from '@fiverr-private/futile/lib/send/sendGraph';
import PreviewMessage from '../Previewer/components/PreviewMessage';
import reportToBigQuery from '../Utils/bigQueryReporter';
import { sendMixPanelEvent } from '../Utils/mixpanel';
import { OPENED_FILE_VIEWER } from '../Utils/mixpanel/mixpanelEvents';
import FilerrShape from '../Previewer/PropsShape/FilerrShape';
import * as SettingsSelectors from '../Previewer/selectors/settings';
import * as Viewers from './Viewers';
import FileNotSupported from './FileNotSupported';
import FileNotReady from './FileNotReady';
import ErrorOccurred from './ErrorOccurred';
import Context from './context';
import './style.scss';
import { PDF_TYPE } from './consts';

const FILE_PROCESSING = 'processing';

class MediaViewer extends Component {
    constructor(props) {
        super(props);

        this.getViewerByFile = this.getViewerByFile.bind(this);
        this.updateCurrentViewerByFile = this.updateCurrentViewerByFile.bind(this);
        this.onMediaLoad = this.onMediaLoad.bind(this);
        this.reportViewerLoadEvents = this.reportViewerLoadEvents.bind(this);
        this.onError = this.onError.bind(this);
        this.updateMarkersInContext = this.updateMarkersInContext.bind(this);
        this.setEmbeddedElementRef = this.setEmbeddedElementRef.bind(this);
        this.onResizeHandler = this.onResizeHandler.bind(this);
        this.rebindObserver = this.rebindObserver.bind(this);
        this.setMediaViewRef = this.setMediaViewRef.bind(this);
        this.resizeObserver = new ResizeObserver(this.onResizeHandler);

        this.state = {
            hasForceError: false,
            previewMessageStyle: null,
            context: {
                onMediaLoad: this.onMediaLoad,
                onPlay: props.onPlay,
                onMarkerClick: props.onMarkerClick,
                markers: props.markers,
                markersTemplate: props.markersTemplate,
                onError: this.onError,
                setEmbeddedElementRef: this.setEmbeddedElementRef,
                onProgressClick: props.onProgressClick
            }
        };
    }

    componentDidMount() {
        this.updateCurrentViewerByFile();
        this.reportViewerLoadEvents();
        window.addEventListener('resize', this.onResizeHandler);
        this.rebindObserver();
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.filerr !== prevProps.filerr) {
            this.updateCurrentViewerByFile().then(this.reportViewerLoadEvents).catch((err) => err);
            this.rebindObserver();
            this.onResizeHandler();
        }

        if (this.state.hasForceError && !prevState.hasForceError) {
            this.reportViewerLoadEvents();
        }

        if (!this.state.hasForceError && prevState.hasForceError) {
            this.rebindObserver();
            this.onResizeHandler();
        }

        if (this.props.markers !== prevProps.markers) {
            this.updateMarkersInContext();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onResizeHandler);
        this.resizeObserver.disconnect();
    }

    rebindObserver() {
        if (this.embeddedElementRef) {
            this.resizeObserver.observe(this.embeddedElementRef);
        }
    }

    setEmbeddedElementRef(ref) {
        if (ref) {
            this.embeddedElementRef = ref;
        }
    }

    setMediaViewRef(ref) {
        if (ref) {
            this.mediaViewerRef = ref;
        }
    }

    onResizeHandler() {
        const { filerr } = this.props;
        if (!filerr.previewMessage || !this.embeddedElementRef) {
            return;
        }

        const embeddedElementPos = this.embeddedElementRef.getBoundingClientRect();

        this.setState({
            previewMessageStyle: {
                visibility: 'initial',
                bottom: embeddedElementPos.top - 25
            }
        });
    }

    updateMarkersInContext() {
        this.setState({
            context: {
                ...this.state.context,
                markers: this.props.markers
            }
        });
    }

    onMediaLoad(payload) {
        const { onLoad } = this.props;
        onLoad && onLoad(payload);
        this.setState({ isLoading: false });
    }

    updateCurrentViewerByFile() {
        return new Promise((resolve) => {
            const currentViewer = this.getViewerByFile(this.props.filerr);
            const isMediaViewer = Object.values(Viewers).indexOf(currentViewer) > -1;

            this.onMediaLoad(null);

            this.setState({
                isLoading: isMediaViewer,
                hasForceError: false
            }, resolve);
        });
    }

    reportViewerLoadEvents() {
        const {
            filerr,
            source
        } = this.props;

        const currentViewer = this.getViewerByFile(filerr);
        const { id, previewType, previewState } = filerr || {};

        if (currentViewer === FileNotSupported) {
            reportToBigQuery({
                group: 'collaboration',
                type: 'seen_file_preview_not_supported'
            });

            sendMixPanelEvent({
                eventName: OPENED_FILE_VIEWER,
                eventData: {
                    'File Type': previewType,
                    'File state': 'Not Supported',
                    'Related Flow': source
                }
            });
        } else if (currentViewer === ErrorOccurred) {
            reportToBigQuery({
                group: 'collaboration',
                type: 'seen_file_preview_error'
            });

            sendMixPanelEvent({
                eventName: OPENED_FILE_VIEWER,
                eventData: {
                    'File Type': previewType,
                    'File state': 'Error Occurred',
                    'Attachment Id': id,
                    'Related Flow': source
                }
            });

            sendGraph({ label:'collaboration.file_preview_error' });

        } else if (previewState && previewState === FILE_PROCESSING) {
            sendMixPanelEvent({
                eventName: OPENED_FILE_VIEWER,
                eventData: {
                    'File Type': previewType,
                    'File state': 'Pending',
                    'Related Flow': source
                }
            });
        } else {
            reportToBigQuery({
                group: 'collaboration',
                type: 'clicked_file_preview_show'
            });

            sendMixPanelEvent({
                eventName: OPENED_FILE_VIEWER,
                eventData: {
                    'File Type': previewType,
                    'File state': 'Ready',
                    'Related Flow': source
                }
            });
        }
    }

    getViewerByFile(filerr) {
        const { eligibleToPdfViewer } = this.props;
        if (!filerr || this.state.hasForceError) {
            return ErrorOccurred;
        }

        const { previewState, previewType } = filerr,
            ViewerName = startCase(previewType),
            Viewer = Viewers[ViewerName]; // eslint-disable-line

        // If the file is not ready yet, we show a "File Not Ready" screen
        if (previewState && previewState === FILE_PROCESSING) {
            return FileNotReady;
        }

        if (previewType === PDF_TYPE && !eligibleToPdfViewer) {
            return FileNotSupported;
        }

        return Viewer || FileNotSupported;
    }

    onError() {
        this.setState({
            hasForceError: true,
            isLoading: false
        });
    }

    render() {
        const {
            isLoading,
            previewMessageStyle,
            currentViewerWrapperStyle
        } = this.state;

        const {
            filerr
        } = this.props;

        const CurrentViewer = this.getViewerByFile(filerr);
        const classes = classNames('media-viewer-package', { loading: isLoading });

        return (
            <Context.Provider value={this.state.context}>
                <div className={classes} ref={this.setMediaViewRef}>
                    {filerr && filerr.previewMessage && (
                        <PreviewMessage text={filerr.previewMessage}
                            style={previewMessageStyle}
                            elementRef={this.setPreviewMessageRef}/>
                    )}
                    <div className="current-viewer-wrapper" style={currentViewerWrapperStyle}>
                        <CurrentViewer filerr={filerr}/>
                    </div>
                    {/* {isImage && <Toolbar extraControls={extraControls}/>} */}
                </div>
            </Context.Provider>
        );
    }
}

MediaViewer.propTypes = {
    source: PropTypes.string,
    filerr: FilerrShape.isRequired,
    onLoad: PropTypes.func,
    markers: PropTypes.array,
    markersTemplate: PropTypes.func,
    onMarkerClick: PropTypes.func,
    onProgressClick: PropTypes.func,
    onPlay: PropTypes.func,
    eligibleToPdfViewer: PropTypes.bool
};

const mapStateToProps = (state) => ({
    eligibleToPdfViewer: SettingsSelectors.eligibleForPdfViewer(state)
});

MediaViewer.defaultProps = {
    source: 'Not Defined',
    onLoad: () => null,
    extraControls: null,
    onMarkerClick: () => null,
    onProgressClick: () => null,
    onPlay: () => null
};

export default connect(mapStateToProps)(MediaViewer);
